import { apiClient } from '@/services/api';
import type {
  ReferralLink,
  ReferralLinkKind,
  ReferralLinksBatchResponse,
  ReferralLinksListResponse,
} from '@/types/referral';

export interface ReferralLinksListParams {
  includeArchived?: boolean;
}

export interface ReferralLinkCreateInput {
  kind: ReferralLinkKind;
  label?: string;
  default_channel?: string;
  recipient_contact?: string;
  /**
   * Attribution tag ('qr' today). Server-side this turns create into
   * find-or-create: a partial unique index on (user, deal, source) makes
   * the mint idempotent, so a double-tap returns the same link.
   */
  source?: string;
}

export interface ReferralLinksBatchCreateInput {
  /** All links share a single deal context for the batch. Required by the server (422 otherwise). */
  deal_id: number;
  links: ReferralLinkCreateInput[];
}

export interface ReferralLinkUpdateInput {
  label?: string;
  default_channel?: string;
}

export const referralLinksService = {
  /**
   * GET /api/v1/referral_links — current user's referral links. Response
   * includes decrypted `recipient_contact` + `last_messages_opened_at`
   * for the owner. `Cache-Control: no-store` is set on the response to
   * keep PII out of intermediate caches.
   */
  async list(params: ReferralLinksListParams = {}): Promise<ReferralLinksListResponse> {
    const query = new URLSearchParams();
    if (params.includeArchived) query.set('include_archived', 'true');
    const suffix = query.toString();
    const path = suffix ? `/api/v1/referral_links?${suffix}` : '/api/v1/referral_links';
    return apiClient.get<ReferralLinksListResponse>(path);
  },

  /**
   * POST /api/v1/referral_links/generic — find-or-mint the user's single
   * app-invite link (kind: generic, no deal). Idempotent: repeat calls
   * return the same row, one-per-user is enforced server-side.
   */
  async generic(): Promise<ReferralLink> {
    const { referral_link } = await apiClient.post<{ referral_link: ReferralLink }>(
      '/api/v1/referral_links/generic',
      {},
    );
    return referral_link;
  },

  /**
   * POST /api/v1/referral_links — array-only batch create. Single-link
   * callers wrap their input in a 1-element array. Atomic transaction:
   * one invalid item rolls back ALL. Generic links are not allowed in
   * the array (server rejects).
   */
  async create(input: ReferralLinksBatchCreateInput): Promise<ReferralLinksBatchResponse> {
    return apiClient.post<ReferralLinksBatchResponse>('/api/v1/referral_links', input);
  },

  /**
   * Convenience wrapper for the common single-link case. Wraps the
   * input as a 1-element array and unwraps the response. Use this from
   * existing callers (TrackedLinkCreateSheet, ShareMenu auto-create).
   */
  async createOne(input: ReferralLinkCreateInput, deal_id: number): Promise<ReferralLink> {
    const response = await this.create({ links: [input], deal_id });
    const created = response.referral_links[0];
    if (!created) {
      throw new Error('referral_links#create returned an empty array');
    }
    return created;
  },

  /**
   * PATCH /api/v1/referral_links/:id — edits display metadata only
   * (`label`, `default_channel`). `kind` and `token` are immutable.
   */
  async update(id: number, input: ReferralLinkUpdateInput): Promise<ReferralLink> {
    const response = await apiClient.patch<{ referral_link: ReferralLink }>(
      `/api/v1/referral_links/${id}`,
      input,
    );
    return response.referral_link;
  },

  /**
   * POST /api/v1/referral_links/:id/archive — soft-archive a tracked
   * link. Legacy generic links cannot be archived (server returns 422).
   */
  async archive(id: number): Promise<ReferralLink> {
    const response = await apiClient.post<{ referral_link: ReferralLink }>(
      `/api/v1/referral_links/${id}/archive`,
      {},
    );
    return response.referral_link;
  },
};
